import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Package, ShoppingBag, AlertCircle } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { message } from "antd";
import BottomNavBar from "../../components/BottomNavBar";
import welcomeBg from "../../assets/homebg.png";
import type { SimplifiedProduct } from "../../services/barcodeApiService";

const BarcodeProductDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState<SimplifiedProduct | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get product from location state
    const productData = location.state?.product as SimplifiedProduct;
    
    if (!productData) {
      message.error("No product data found");
      navigate(-1);
      return;
    }

    setProduct(productData);
    setLoading(false);
  }, [location, navigate]);

  const getHealthColorClass = (score: number) => {
    if (score >= 8) return "text-green-600 bg-green-100 dark:bg-green-900/30";
    if (score >= 6) return "text-blue-600 bg-blue-100 dark:bg-blue-900/30";
    if (score >= 4) return "text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30";
    return "text-red-600 bg-red-100 dark:bg-red-900/30";
  };

  const getGradeColorClass = (grade: string) => {
    const lowerGrade = grade.toLowerCase();
    if (lowerGrade === 'a') return "bg-green-500";
    if (lowerGrade === 'b') return "bg-blue-500";
    if (lowerGrade === 'c') return "bg-yellow-500";
    if (lowerGrade === 'd') return "bg-orange-500";
    if (lowerGrade === 'e') return "bg-red-500";
    return "bg-gray-500";
  };

  if (loading || !product) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-subhead text-muted-foreground">Loading product...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-20"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="h-10 w-10 rounded-xl border-border bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title2 font-bold text-foreground">Product Details</h1>
          <div className="h-10 w-10" />
        </div>

        {/* Product Image */}
        {product.image && (
          <Card className="rounded-2xl border-0 bg-card p-4 shadow-sm">
            <img
              src={product.image}
              alt={product.name}
              className="h-48 w-full rounded-xl object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect fill='%23f0f0f0' width='200' height='200'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='monospace' font-size='16' fill='%23999'%3ENo Image%3C/text%3E%3C/svg%3E";
              }}
            />
          </Card>
        )}

        {/* Product Info */}
        <Card className="rounded-2xl border-0 bg-card p-6 shadow-sm">
          <div className="mb-4">
            <h2 className="break-words text-title1 font-bold leading-snug text-foreground">{product.name}</h2>
            {product.brand && product.brand !== "Unknown Brand" && (
              <div className="mt-2 flex items-center gap-2 text-subhead text-muted-foreground">
                <ShoppingBag className="h-4 w-4" />
                <span>{product.brand}</span>
              </div>
            )}
            <div className="mt-2 flex items-center gap-2 text-caption1 text-muted-foreground">
              <Package className="h-3.5 w-3.5" />
              <span>Barcode: {product.barcode}</span>
            </div>
          </div>

          {/* Health Score */}
          <div className="mb-4 flex items-center justify-between rounded-xl border-2 border-border bg-muted/20 p-4">
            <div>
              <p className="text-subhead text-muted-foreground">Health Score</p>
              <p className="text-large-title font-bold text-foreground">{product.health_score}/10</p>
            </div>
            {product.nutrition_grade && product.nutrition_grade !== 'unknown' && (
              <div className={`flex h-16 w-16 items-center justify-center rounded-full ${getGradeColorClass(product.nutrition_grade)} text-title1 font-bold text-white`}>
                {product.nutrition_grade.toUpperCase()}
              </div>
            )}
          </div>

          {/* Health Recommendation */}
          <div className={`mb-4 flex items-start gap-3 rounded-xl p-4 ${getHealthColorClass(product.health_score)}`}>
            <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-subhead">
                {product.health_score >= 8 ? "Great choice!" :
                 product.health_score >= 6 ? "Good option" :
                 product.health_score >= 4 ? "Consume occasionally" :
                 "⚠️ Not recommended"}
              </p>
              <p className="text-caption1 mt-1 opacity-90">
                {product.health_score >= 8 ? "This product has good nutritional value." :
                 product.health_score >= 6 ? "Generally healthy. Enjoy in moderation." :
                 product.health_score >= 4 ? "Consider healthier alternatives." :
                 "High in sugar/fat. Not for regular consumption."}
              </p>
            </div>
          </div>
        </Card>

        {/* Nutrition Facts */}
        <Card className="rounded-2xl border-0 bg-card p-6 shadow-sm">
          <h3 className="mb-4 text-title3 font-bold text-foreground">Nutrition Facts</h3>
          <p className="mb-3 text-caption1 text-muted-foreground">Per {product.serving_size}</p>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="text-subhead font-medium text-foreground">Calories</span>
              <span className="text-subhead font-bold text-orange-600">{Math.round(product.calories)} kcal</span>
            </div>
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="text-subhead font-medium text-foreground">Carbohydrates</span>
              <span className="text-subhead font-bold text-blue-600">{product.carbs.toFixed(1)}g</span>
            </div>
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="text-subhead font-medium text-foreground">Sugar</span>
              <span className={`text-subhead font-bold ${product.sugar > 10 ? 'text-red-600' : 'text-green-600'}`}>
                {product.sugar.toFixed(1)}g
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="text-subhead font-medium text-foreground">Protein</span>
              <span className="text-subhead font-bold text-purple-600">{product.protein.toFixed(1)}g</span>
            </div>
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="text-subhead font-medium text-foreground">Fat</span>
              <span className={`text-subhead font-bold ${product.fat > 15 ? 'text-red-600' : 'text-green-600'}`}>
                {product.fat.toFixed(1)}g
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-subhead font-medium text-foreground">Fiber</span>
              <span className="text-subhead font-bold text-green-600">{product.fiber.toFixed(1)}g</span>
            </div>
          </div>
        </Card>

        {/* Ingredients */}
        {product.ingredients && product.ingredients !== "Not available" && (
          <Card className="rounded-2xl border-0 bg-card p-6 shadow-sm">
            <h3 className="mb-3 text-title3 font-bold text-foreground">Ingredients</h3>
            <p className="text-subhead leading-relaxed text-muted-foreground">
              {product.ingredients}
            </p>
          </Card>
        )}
      </div>

      <BottomNavBar activeIndex={-1} />
    </div>
  );
};

export default BarcodeProductDetail;
